import { TrendingDown, ShieldCheck, ArrowUpFromLine, BarChart3 } from "lucide-react";
import { formatNumber, getTickerCurrency } from "../utils/trading";

const SIG_COLORS = {
  STRONG_BUY: "text-emerald-400 bg-emerald-500/15 border-emerald-500/25",
  BUY: "text-green-400 bg-green-500/10 border-green-500/20",
  WATCHLIST: "text-amber-400 bg-amber-500/10 border-amber-500/20",
};

function ConfirmDots({ opp }) {
  const items = [
    { key: "support_detected", label: "S", title: "Support" },
    { key: "rebound_confirmed", label: "R", title: "Rebond" },
    { key: "volume_confirmed", label: "V", title: "Volume" },
    { key: "rr_valid", label: "RR", title: "Risk/Reward" },
  ];
  return (
    <div className="flex gap-0.5" data-testid="confirm-dots">
      {items.map(({ key, label, title }) => (
        <span
          key={key}
          title={title}
          className={`text-[6px] font-black w-3 h-3 flex items-center justify-center rounded-sm ${
            opp[key] ? "bg-emerald-500/25 text-emerald-400" : "bg-slate-700/40 text-slate-600"
          }`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

export default function PullbackCompact({ opportunities, onAnalyze, loading }) {
  if (loading) return (
    <div className="glass-card rounded-xl p-3" data-testid="pullback-compact">
      <div className="flex items-center gap-1.5 mb-2">
        <TrendingDown className="w-3.5 h-3.5 text-orange-400" />
        <span className="text-xs font-bold">Pullback Opportunities</span>
      </div>
      <div className="text-[10px] text-slate-500 text-center py-4">Scan en cours...</div>
    </div>
  );

  if (!opportunities || opportunities.length === 0) return (
    <div className="glass-card rounded-xl p-3" data-testid="pullback-compact">
      <div className="flex items-center gap-1.5 mb-2">
        <TrendingDown className="w-3.5 h-3.5 text-orange-400" />
        <span className="text-xs font-bold">Pullback Opportunities</span>
      </div>
      <div className="text-[10px] text-slate-500 text-center py-4">Aucun pullback confirme</div>
    </div>
  );

  // Prioritize STRONG_BUY, then BUY, then WATCHLIST
  const signalOrder = { STRONG_BUY: 0, BUY: 1, WATCHLIST: 2 };
  const sorted = [...opportunities].sort((a, b) => {
    const sa = signalOrder[a.signal] ?? 3;
    const sb = signalOrder[b.signal] ?? 3;
    if (sa !== sb) return sa - sb;
    return (b.pullback_confirmation_score || 0) - (a.pullback_confirmation_score || 0);
  });

  const strongBuys = sorted.filter(o => o.signal === "STRONG_BUY").length;
  const buys = sorted.filter(o => o.signal === "BUY").length;
  const top = sorted.slice(0, 8);

  return (
    <div className="glass-card rounded-xl p-3" data-testid="pullback-compact">
      <div className="flex items-center gap-1.5 mb-2">
        <TrendingDown className="w-3.5 h-3.5 text-orange-400" />
        <span className="text-xs font-bold">Pullback Opportunities</span>
        <span className="text-[8px] text-slate-500 ml-auto">
          {strongBuys > 0 && <span className="text-emerald-400 font-bold">{strongBuys} STRONG</span>}
          {strongBuys > 0 && buys > 0 && " / "}
          {buys > 0 && <span className="text-green-400">{buys} BUY</span>}
          {(strongBuys > 0 || buys > 0) && " / "}
          <span className="text-slate-500">{opportunities.length} total</span>
        </span>
      </div>
      <div className="space-y-px">
        {top.map((opp) => {
          const curr = getTickerCurrency(opp.ticker);
          const sig = SIG_COLORS[opp.signal] || SIG_COLORS.WATCHLIST;
          const score = opp.pullback_confirmation_score || 0;
          return (
            <div key={opp.ticker}
              onClick={() => onAnalyze?.(opp.ticker)}
              className="flex items-center gap-1 px-1.5 py-1 rounded hover:bg-slate-700/40 cursor-pointer transition-colors group"
              data-testid={`pullback-compact-${opp.ticker}`}>
              <span className="text-[10px] w-3 shrink-0">{opp.flag}</span>
              <span className="font-mono text-[10px] font-bold text-cyan-400 group-hover:text-cyan-300 truncate min-w-0">{opp.ticker}</span>
              <span className={`text-[6px] font-bold px-0.5 rounded border shrink-0 leading-tight ${sig}`}>
                {opp.signal?.replace("_", " ")}
              </span>
              <span className={`text-[7px] font-black px-1 rounded-full shrink-0 ${
                score >= 3 ? "bg-emerald-500/20 text-emerald-400" : score >= 2 ? "bg-amber-500/15 text-amber-400" : "bg-slate-700/30 text-slate-500"
              }`}>
                {score}/4
              </span>
              <ConfirmDots opp={opp} />
              <span className="ml-auto font-mono text-[10px] text-white shrink-0">{curr}{formatNumber(opp.price)}</span>
              <span className="font-mono text-[9px] font-bold text-orange-400 shrink-0 w-10 text-right">
                RSI {opp.rsi}
              </span>
              <span className="font-mono text-[9px] text-red-400 shrink-0 w-9 text-right">
                {opp.pullback_pct > 0 ? "+" : ""}{opp.pullback_pct}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
